import React from 'react';
import { polishTagGroups, PolishTagGroup } from './polishBookTags';

const formatCount = (count: number) => count.toLocaleString('en-US');

function TagTable({ group, genreTitle, index }: { group: PolishTagGroup; genreTitle: string; index: number }) {
  const headingId = `${group.genre}-tags-${index}`;
  return <section aria-labelledby={headingId}>
    <h3 id={headingId}>{group.title}</h3>
    <div className="publishing-guide-table polish-book-tags-table" role="region" aria-label={`${genreTitle}: ${group.title}`} tabIndex={0}>
      <table>
        <caption>{genreTitle} · {group.title}</caption>
        <thead><tr>
          <th scope="col">Tag on Lubimyczytać</th>
          <th scope="col">English meaning</th>
          <th scope="col">Books</th>
          <th scope="col">Readers</th>
          <th scope="col">Most-rated book with the tag</th>
        </tr></thead>
        <tbody>{group.rows.map(([tag, englishTag, meaning, books, readers, book]) => <tr key={tag}>
          <th scope="row"><span lang={englishTag ? 'en' : 'pl'}>{tag}</span>{englishTag && <> <abbr title="English tag on the site">EN</abbr></>}</th>
          <td>{meaning}</td>
          <td className="polish-book-tags-count">{formatCount(books)}</td>
          <td className="polish-book-tags-count">{formatCount(readers)}</td>
          <td>{book}</td>
        </tr>)}</tbody>
      </table>
    </div>
  </section>;
}

export default function PolishBookTagsGuideBody() {
  return <div className="publishing-guide-body">
    <h2>Reading the tables</h2>
    <p>Which tags Polish readers use on Lubimyczytać, and the readership associated with books carrying each tag. Within each group, tags are sorted by readers.</p>
    <ul>
      <li><strong>Books:</strong> how many books on the site carry the tag. An indication of how widely the tag is used.</li>
      <li><strong>Readers:</strong> people with the book on a shelf (Czytelnicy), summed over the tag’s 30 most-rated books. An indication of the tagged books’ reach.</li>
      <li><strong>EN:</strong> the tag is English on the site, so it needs no translation. English tags often come from imported English editions with few Polish readers.</li>
    </ul>
    <p>Source: logged-out Lubimyczytać tag pages, 2–3 November 2026. Tags are typed by users, so spelling variants split the counts. Most-rated book is the first title on the tag page, by number of ratings. Book titles and author names abbreviated in the source are shown with an ellipsis.</p>
    <p>These figures reflect activity on Lubimyczytać and are intended to guide keyword research; they do not measure retailer searches or sales.</p>
    <nav aria-label="In this guide"><a href="#dark-romance-tags">Dark romance</a> · <a href="#romantasy-tags">Romantasy</a></nav>
    <section aria-labelledby="dark-romance-tags">
      <h2 id="dark-romance-tags">Dark romance</h2>
      <p>Polish theme words (zemsta, obsesja, porwanie) and romans mafijny have high reader counts. Readers coined their own hate-love, which has a higher reader count than hate to love. Popular English trope labels such as morally gray and mafia boss have low reader counts in this sample.</p>
      {polishTagGroups.filter(group => group.genre === 'dark-romance').map((group, index) => <TagTable key={group.title} group={group} genreTitle="Dark romance" index={index} />)}
    </section>
    <section aria-labelledby="romantasy-tags">
      <h2 id="romantasy-tags">Romantasy</h2>
      <p>Here the English genre labels stand out: romantasy, fae and paranormal romance all have large readerships. Creature tags have higher reader counts in Polish (smoki, czarownice, zmiennokształtni). Several BookTok tags, such as fated mates, dark romantasy and monster romance, have lower reader counts in this sample. Broad tags like magia and przeznaczenie are led by non-romance books, so pair them with a genre label.</p>
      {polishTagGroups.filter(group => group.genre === 'romantasy').map((group, index) => <TagTable key={group.title} group={group} genreTitle="Romantasy" index={index} />)}
    </section>
  </div>;
}
